import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import Tilt from 'react-parallax-tilt';
import Barcode from 'react-barcode';
import { Box, Typography, Avatar, CircularProgress, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Slider } from '@mui/material';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../utils/cropImage';
import SchoolIcon from '@mui/icons-material/School';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import axios from 'axios';

const StudentIdCard = ({ currentUser }) => {
    const initials = currentUser?.name?.substring(0, 2).toUpperCase() || 'ST';
    const [uploading, setUploading] = useState(false);
    
    // Fallback logo replaced by SchoolIcon for 100% reliability
    const hasProfilePic = currentUser?.profilePicture && currentUser.profilePicture.length > 10;
    
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [openCropDialog, setOpenCropDialog] = useState(false);

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Reset crop settings
        setCrop({ x: 0, y: 0 });
        setZoom(1);

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
            setImageSrc(reader.result);
            setOpenCropDialog(true);
        };
        
        // Clear input value so same file can be selected again
        e.target.value = '';
    };

    const handleCropSave = async () => {
        try {
            setOpenCropDialog(false);
            setUploading(true);
            
            // Get cropped image
            const croppedImageBase64 = await getCroppedImg(
                imageSrc,
                croppedAreaPixels
            );
            
            await axios.put(`${process.env.REACT_APP_BASE_URL}/Student/${currentUser._id}`, {
                profilePicture: croppedImageBase64
            });
            
            // Update local storage so it reflects immediately on refresh
            const updatedUser = { ...currentUser, profilePicture: croppedImageBase64 };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            window.location.reload();
        } catch (error) {
            console.error("Error uploading cropped image:", error);
            setUploading(false);
        }
    };

    const handleCropCancel = () => {
        setOpenCropDialog(false);
        setImageSrc(null);
    };

    return (
        <Tilt
            className="parallax-effect-glare-scale"
            perspective={1000}
            glareEnable={true}
            glareMaxOpacity={0.3}
            scale={1.03}
            gyroscope={true}
            style={{ width: '100%', maxWidth: '310px', margin: '0 auto' }}
        >
            <CardContainer>
                <PunchHole />
                <HeaderSection>
                    <LogoContainer>
                        <SchoolIcon sx={{ fontSize: 32, color: '#0D47A1' }} />
                    </LogoContainer>
                    <HeaderTitle>INDIAN INSTITUTE OF TECHNOLOGY PATNA</HeaderTitle>
                </HeaderSection>
                
                <AvatarContainer>
                    <input 
                        accept="image/*" 
                        id="icon-button-file" 
                        type="file" 
                        style={{ display: 'none' }}
                        onChange={handleImageUpload}
                    />
                    <label htmlFor="icon-button-file">
                        <Tooltip title="Upload Photo" placement="right">
                            <IconButton component="span" sx={{ padding: 0, position: 'relative' }}>
                                <StyledAvatar src={hasProfilePic ? currentUser.profilePicture : undefined}>
                                    {!hasProfilePic && !uploading && initials}
                                    {uploading && <CircularProgress size={40} color="inherit" />}
                                </StyledAvatar>
                                <CameraOverlay>
                                    <PhotoCameraIcon sx={{ color: 'white', fontSize: 20 }} />
                                </CameraOverlay>
                            </IconButton>
                        </Tooltip>
                    </label>
                </AvatarContainer>
                
                <BodySection>
                    <NameText>{currentUser?.name?.toUpperCase()}</NameText>
                    <RoleBadge>STUDENT</RoleBadge>
                    
                    <DetailsGrid>
                        <DetailItem>
                            <DetailLabel>ROLL NO</DetailLabel>
                            <DetailValue>{currentUser?.rollNum}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailLabel>CLASS</DetailLabel>
                            <DetailValue>{currentUser?.sclassName?.sclassName || 'B.Tech'}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailLabel>BLOOD GRP</DetailLabel>
                            <DetailValue>O+</DetailValue>
                        </DetailItem>
                    </DetailsGrid>
                </BodySection>
                
                <FooterSection>
                    <Barcode 
                        value={currentUser?.rollNum ? `IITP-${currentUser.rollNum}` : 'IITP-STUDENT'} 
                        width={1.5} 
                        height={35} 
                        displayValue={false}
                        background="transparent"
                        lineColor="#ffffff"
                    />
                    <FooterText>Property of IIT Patna</FooterText>
                </FooterSection>
            </CardContainer>

            {/* Image Crop Dialog */}
            <Dialog open={openCropDialog} onClose={handleCropCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Crop Profile Photo</DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ position: 'relative', width: '100%', height: 300, background: '#333' }}>
                        {imageSrc && (
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1} // Square aspect ratio for avatar
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                cropShape="round"
                                showGrid={false}
                            />
                        )}
                    </Box>
                    <Box sx={{ mt: 3 }}>
                        <Typography gutterBottom>Zoom</Typography>
                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e, zoom) => setZoom(zoom)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCropCancel} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleCropSave} variant="contained" color="primary">
                        Save Photo
                    </Button>
                </DialogActions>
            </Dialog>
        </Tilt>
    );
};

export default StudentIdCard;

// STYLES

const CardContainer = styled(Box)`
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 15px 35px rgba(0,0,0,0.15), 0 5px 15px rgba(0,0,0,0.08);
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid rgba(0,0,0,0.05);
`;

const PunchHole = styled(Box)`
    position: absolute;
    top: 12px;
    width: 60px;
    height: 10px;
    background: #e0e0e0;
    border-radius: 10px;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);
    z-index: 10;
`;

const HeaderSection = styled(Box)`
    background: linear-gradient(135deg, #0D47A1 0%, #1976D2 100%);
    width: 100%;
    padding: 35px 20px 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const LogoContainer = styled(Box)`
    width: 50px;
    height: 50px;
    margin-bottom: 10px;
    background: #ffffff;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
`;

const HeaderTitle = styled(Typography)`
    font-size: 0.85rem !important;
    font-weight: 800 !important;
    color: #ffffff;
    line-height: 1.2 !important;
    letter-spacing: 0.5px;
`;

const AvatarContainer = styled(Box)`
    margin-top: -45px;
    margin-bottom: 15px;
    z-index: 2;
    padding: 6px;
    background: #ffffff;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const StyledAvatar = styled(Avatar)`
    width: 90px !important;
    height: 90px !important;
    font-size: 2.5rem !important;
    background: linear-gradient(45deg, #FF9933, #FFB74D) !important;
    color: white !important;
    font-weight: bold;
    transition: all 0.3s ease;
`;

const CameraOverlay = styled(Box)`
    position: absolute;
    bottom: 0;
    right: 0;
    background: #0D47A1;
    border-radius: 50%;
    padding: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid white;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    transition: all 0.2s;
    
    &:hover {
        background: #1976D2;
        transform: scale(1.1);
    }
`;

const BodySection = styled(Box)`
    width: 100%;
    padding: 0 25px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const NameText = styled(Typography)`
    font-size: 1.4rem !important;
    font-weight: 800 !important;
    color: #0A192F;
    margin-bottom: 4px !important;
    text-align: center;
    line-height: 1.2 !important;
`;

const RoleBadge = styled(Box)`
    background: rgba(255, 153, 51, 0.15);
    color: #E65100;
    padding: 4px 14px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 1px;
    margin-bottom: 20px;
`;

const DetailsGrid = styled(Box)`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #f8fafc;
    padding: 15px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
`;

const DetailItem = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px dashed #cbd5e1;
    padding-bottom: 4px;
    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const DetailLabel = styled(Typography)`
    font-size: 0.75rem !important;
    font-weight: 700 !important;
    color: #64748b;
`;

const DetailValue = styled(Typography)`
    font-size: 0.85rem !important;
    font-weight: 800 !important;
    color: #0f172a;
`;

const FooterSection = styled(Box)`
    background: #0A192F;
    width: 100%;
    padding: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-top: 4px solid #FF9933;
`;

const FooterText = styled(Typography)`
    color: rgba(255,255,255,0.6);
    font-size: 0.65rem !important;
    margin-top: 8px !important;
    text-transform: uppercase;
    letter-spacing: 1px;
`;
